import type { Role } from '@relif/zeller-roles';

import type { QuickReply } from './quickReply';

export interface WorkingHoursRange {
  start: number; // 0-23
  end: number; // 0-23
  days: number[]; // 0 = Sunday, 6 = Saturday
}

export type WorkingHours = WorkingHoursRange[];

// BCI refi seller team, used to route clients by salary. Only relevant for BU 78.
export type AssignmentTeam = 'service_center' | 'sales_force';

export interface SignInBody {
  email: string;
  password: string;
  recaptchaToken: string;
}

export interface MfaChallenge {
  mfaRequired: true;
  mfaToken: string;
  email: string;
}

export type SignInResponse = User | MfaChallenge;

export function isMfaChallenge(response: SignInResponse | undefined): response is MfaChallenge {
  return !!response && (response as MfaChallenge).mfaRequired === true;
}

export interface MfaVerifyBody {
  mfaToken: string;
  code: string;
}

export interface GoogleSignInBody {
  credential: string;
}

export interface MicrosoftSignInBody {
  credential: string;
}

export interface MfaResendBody {
  mfaToken: string;
}

export interface User {
  id: number;
  createdAt: string;
  updatedAt: string;
  email: string;
  accessToken: string;
  isAdmin: boolean;
  filterConversationsByDefault: boolean;
  isTestUser: boolean | null;
  businessUnitIds: number[] | null;
  businessUnitNames?: string[] | null;
  companyId: number;
  company?: {
    id: number;
    name: string;
  };
  name: string | null;
  phone: string | null;
  rut?: string | null;
  canAssignClient: boolean;
  isAssignable: boolean;
  role: Role;
  brandsToSell?: string[] | null;
  branchOffice?: string | null;
  topicsOfInterest?: string[] | null;
  sources?: string[] | null;
  hasPlanerAccess?: boolean;
  isBankExecutive?: boolean | null;
  bankName?: string | null;
  allowedTemplateNames?: string[] | null;
  allowAllTemplates?: boolean;
  allowTemplateCreation?: boolean;
  isAway?: boolean | null;
  description?: string | null;
  externalId?: string | null;
  reportEmailFrequency?: 'daily' | 'weekly' | 'monthly' | null;
  assignedSellerIds?: number[] | null;
  workingHours?: WorkingHours | null;
  isAvailable?: boolean;
  quickReplies?: QuickReply[];
  assignmentTeam?: AssignmentTeam | null;
}

export interface UserAttributes {
  email: string;
  businessUnitIds: number[];
  name?: string | null;
  phone?: string | null;
  rut?: string | null;
  brandsToSell?: string[] | null;
  branchOffice?: string | null;
  sources?: string[] | null;
  role: Role;
  isAssignable?: boolean;
  allowedTemplateNames?: string[] | null;
  allowAllTemplates?: boolean;
  allowTemplateCreation?: boolean;
  reportEmailFrequency?: 'daily' | 'weekly' | 'monthly' | null;
  assignedSellerIds?: number[] | null;
  workingHours?: WorkingHours | null;
  isAway?: boolean;
  assignmentTeam?: AssignmentTeam | null;
}
